import React from 'react';
import './ElectEmb.css';

interface ElectEmbProps {
  /** Optional message below the spinner */
  message?: string;
  /** 'page' = centered block, 'overlay' = fixed fullscreen overlay, 'inline' = inline small spinner */
  mode?: 'page' | 'overlay' | 'inline';
  /** Spinner size in px (default: 100 for page/overlay, 18 for inline) */
  size?: number;
}

/* Arc geometry — viewBox is always 0 0 100 100 */
const R = 40;
const STROKE_W = 7;
const CIRCUMFERENCE = 2 * Math.PI * R; // ~251.33
const ARC_LEN = (110 / 360) * CIRCUMFERENCE; // ~76.8
const DASH = `${ARC_LEN} ${CIRCUMFERENCE - ARC_LEN}`;

const ARCS = [
  { color: '#a8d0ea', offset: 0 },                         // pastel blue
  { color: '#80d8b4', offset: -(CIRCUMFERENCE / 3) },      // mint green
  { color: '#ee8890', offset: -(CIRCUMFERENCE / 3) * 2 },  // coral pink
];

const ElectEmb: React.FC<ElectEmbProps> = ({ message, mode = 'page', size }) => {
  const resolvedSize = size ?? (mode === 'inline' ? 18 : 100);

  const showCenterText = mode !== 'inline' && resolvedSize >= 50;

  const spinner = (
    <svg
      className="electemb-svg"
      width={resolvedSize}
      height={resolvedSize}
      viewBox="0 0 100 100"
    >
      <g className="electemb-arcs">
        {ARCS.map((a, i) => (
          <circle
            key={i}
            cx={50}
            cy={50}
            r={R}
            fill="none"
            stroke={a.color}
            strokeWidth={STROKE_W}
            strokeLinecap="round"
            strokeDasharray={DASH}
            strokeDashoffset={a.offset}
          />
        ))}
      </g>
      {showCenterText && (
        <text
          x={50}
          y={54}
          textAnchor="middle"
          className="electemb-center-text"
        >
          Loading…
        </text>
      )}
    </svg>
  );

  if (mode === 'inline') {
    return <span className="electemb-inline">{spinner}</span>;
  }

  if (mode === 'overlay') {
    return (
      <div className="electemb-overlay">
        <div className="electemb-overlay-card">
          {spinner}
          {message && <span className="electemb-message">{message}</span>}
        </div>
      </div>
    );
  }

  // page mode (default)
  return (
    <div className="electemb-page">
      {spinner}
      {message && <span className="electemb-message">{message}</span>}
    </div>
  );
};

export default ElectEmb;
